import generateUUID from '../../src/react/helpers/generateUUID';
import {
  railsStudentCaseUnlockContinue,
  caseExperienceApplyStateUrl,
  caseExperiencePatchStateUrl,
  caseExperienceUnlockUrl,
  caseExperienceSwitchSectionUrl,
  withCaseExperienceAdminPreviewMode,
  getCaseExperienceAdminPreviewMode,
} from './case_experience_api';
import { getCsrfToken } from './get_csrf_token';
import {
  documentUserStateResponseUnauthorized,
  rejectDocumentUserStateSaveResponse,
} from './reject_document_user_state_save_response';
import { normalizeNodeUid } from './normalize_node_uid';
import {
  getCasePathContextFromDom,
  getNextVisibleDocSection,
  refreshCaseNextRootSectionUidsInDom,
  rootSectionVisibleOnCasePath,
} from './case_path_visibility';
import { renderCaseExperienceAquiButtonHtml } from './aqui_button_html';
import { fetchAndMountVpeErrorModal, removeDynamicVpeErrorModals } from './vpe_fallback_error_modal';
import { hideCaseCompletionModal } from './case_completion_modal';
import { getDocumentUserStateResetFromDom } from '../clear_local_case_progress';
import { isCaseOfflineEnabled } from '../offline_gating';
import { browserSeemsOffline, mergeCaseState, routeCaseUnlock, runOnCaseStateChain } from './case_section_cache';

const AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT = 5;

// Mirror of Documents::CaseExperience::StateChangePlanner::PASSIVE_ROOT_CARD_CLASSES (Ruby is authoritative).
// Enforced by spec/services/case_experience/passive_root_card_classes_sync_spec.rb
const PASSIVE_ROOT_CARD_CLASSES = [
  'feedback_card',
  'finish_case_card',
  'release_notes_card',
  'case_synopsis_card',
  'saq_card',
  'references_card',
  'learning_objectives_card',
];

const isPassiveRootCardSection = (section) => {
  if (!section?.classList) return false;
  return PASSIVE_ROOT_CARD_CLASSES.some((className) => section.classList.contains(className));
};

const filterAndFlattenActions = (actions) => {
  let filteredActions = actions.filter((action) =>
    Array.isArray(action) ? action.length > 0 : true,
  );
  filteredActions = filteredActions.flat();
  filteredActions = filteredActions.map((action) => {
    if (action.arguments?.uid) {
      action.arguments.uid = normalizeNodeUid(action.arguments.uid);
    }
    return action;
  });
  return filteredActions;
};

const findAnswerCommentInActivePane = (scopeEl) => {
  const root =
    scopeEl?.closest?.('#case-experience-active-pane') ||
    document.getElementById('case-experience-active-pane') ||
    scopeEl;
  if (!root) return null;
  return (
    root.querySelector('.doc-section.answer_comment') ||
    root.querySelector('[id^="slot-"][id$="_ANS_COMMENT"] .doc-section.answer_comment') ||
    root.querySelector('[id$="_ANS_COMMENT"].doc-section')
  );
};

const revealAnswerCommentElement = (answerCommentEl) => {
  if (!answerCommentEl) return;
  const unhide = (el) => {
    if (!el?.classList) return;
    el.classList.remove('hidden', 'collapsed');
    el.style.removeProperty('display');
  };
  unhide(answerCommentEl);
  answerCommentEl.querySelectorAll('.hidden, .collapsed').forEach((el) => {
    // Image/gallery expand modals use class `hidden` while closed; leave them alone.
    if (el.closest?.('.aqui-image-modal')) return;
    unhide(el);
  });
};

const applyCaseExperienceTurboStreamsFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return;
  const turboStreams = payload.turbo_streams;
  if (
    typeof turboStreams === 'string' &&
    turboStreams.trim().length > 0 &&
    typeof window !== 'undefined' &&
    window.Turbo?.renderStreamMessage
  ) {
    window.Turbo.renderStreamMessage(turboStreams);
  }
};

const documentUserStateEndpoint = (documentId, userId) => {
  return `/api/v2/document_user_states/${documentId}/${userId}`;
};

const buildDocumentUserStateRequestBody = (payload) => {
  const body =
    payload.body || {
      document_user_state: {
        state: {
          [normalizeNodeUid(payload.nodeUid)]: {
            [payload.key]: {
              value: payload.value,
              updatedAt: new Date().getTime(),
            },
          },
        },
      },
    };
  body.document_user_state = {
    ...body.document_user_state,
    client_reset: getDocumentUserStateResetFromDom(),
  };
  return body;
};

const mergeDocumentUserStateFromApiResponse = (responseData) => {
  const apiState =
    responseData?.state && typeof responseData.state === 'object' ? responseData.state : {};
  const mergedState = mergeDocumentUserStateForContinueSync(getDusFromDom(), apiState);
  updateDusInDom(mergedState);
  syncAllContinueButtonsForDocument(mergedState);
  return mergedState;
};

let applyStateRequestChain = Promise.resolve();
const patchDocumentUserStateChains = new Map();

const runOnPromiseChain = (chainRef, execute) => {
  const chained = chainRef.current.then(execute, execute);
  chainRef.current = chained.then(
    () => {},
    () => {},
  );
  return chained;
};

const patchChainKey = (documentId, userId) => `${documentId}:${userId}`;

const loadOfflineSync = () => import('../../src/react/helpers/OfflineProgressSync');

const couldNotReachServer = (error) => error?.name === 'TypeError' || error?.name === 'AbortError';

// A click, a submit, and Continue share one chain so each cache write sees the previous one.
const queueCaseState = (payload, body) =>
  runOnCaseStateChain(() => storeCaseStateLocally(payload, body));

const storeCaseStateLocally = async (payload, body) => {
  const submitted = body.document_user_state?.state || {};
  const { default: sync } = await loadOfflineSync();
  const cached = await sync.readCachedDocumentUserState(payload.documentId, payload.userId);
  const alreadySaved = mergeDocumentUserStateForContinueSync(cached?.state, getDusFromDom());
  const state = mergeDocumentUserStateForContinueSync(alreadySaved, submitted);

  await sync.queueDocumentUserStateUpdate(
    payload.documentId,
    payload.userId,
    submitted,
    'api/v2/document_user_states',
    body.document_user_state?.client_reset ?? 0,
  );
  await writeCaseCache(sync, payload.documentId, payload.userId, cached, state);

  updateDusInDom(state);
  syncAllContinueButtonsForDocument(state);
  return { state };
};

const writeCaseCache = async (sync, documentId, userId, cached, state) => {
  try {
    await sync.cacheDocumentUserState(documentId, userId, { ...(cached || {}), state });
  } catch (error) {
    console.warn('[CaseOffline] failed to store case answers', error);
  }
};

// After a successful online save, keep a copy for the next offline visit.
const rememberCaseState = (documentId, userId) => {
  if (!isCaseOfflineEnabled() || !documentId || !userId) return undefined;
  return runOnCaseStateChain(async () => {
    try {
      const { default: sync } = await loadOfflineSync();
      const cached = await sync.readCachedDocumentUserState(documentId, userId);
      const state = mergeDocumentUserStateForContinueSync(cached?.state, getDusFromDom());
      await writeCaseCache(sync, documentId, userId, cached, state);
    } catch (error) {
      console.warn('[CaseOffline] failed to store case answers', error);
    }
  });
};

const saveCaseState = async (payload, url, body) => {
  if (isCaseOfflineEnabled() && browserSeemsOffline()) return queueCaseState(payload, body);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) await rejectDocumentUserStateSaveResponse(response);

    const saved = await response.json();
    mergeDocumentUserStateFromApiResponse(saved);
    await rememberCaseState(payload.documentId, payload.userId);
    return saved;
  } catch (error) {
    // ponytail: a request that never settles (browser says online, no response) still blocks
    // this chain. Upgrade: abort after a timeout and queue the same way.
    if (isCaseOfflineEnabled() && couldNotReachServer(error)) return queueCaseState(payload, body);
    throw error;
  }
};

const executePatchDocumentUserState = (payload) => {
  const body = buildDocumentUserStateRequestBody(payload);
  return saveCaseState(payload, caseExperiencePatchStateUrl(payload.documentId), body);
};

const patchDocumentUserState = async (payload) => {
  const { documentId, userId } = payload;

  const chainKey = patchChainKey(documentId, userId);
  let chainRef = patchDocumentUserStateChains.get(chainKey);
  if (!chainRef) {
    chainRef = { current: Promise.resolve() };
    patchDocumentUserStateChains.set(chainKey, chainRef);
  }
  return runOnPromiseChain(chainRef, () => executePatchDocumentUserState(payload));
};

const applyCaseExperienceState = async (payload) => {
  const body = withCaseExperienceAdminPreviewMode(buildDocumentUserStateRequestBody(payload));

  const executeApplyState = async () => {
    const responseData = await saveCaseState(
      payload,
      caseExperienceApplyStateUrl(payload.documentId),
      body,
    );
    applyCaseExperienceTurboStreamsFromPayload(responseData);
    // Turbo may replace the active pane after the pre-stream sync; re-sync Continue from merged DUS.
    syncAllContinueButtonsForDocument(
      mergeDocumentUserStateForContinueSync(getDusFromDom(), responseData?.state),
    );
    return responseData;
  };

  const chainRef = { current: applyStateRequestChain };
  const chained = runOnPromiseChain(chainRef, executeApplyState);
  applyStateRequestChain = chainRef.current;
  return chained;
};

/** Authoritative transitions (Turbo, visibility, progression). Prefer patchDocumentUserState for value-only saves. */
const updateDocumentUserState = applyCaseExperienceState;

const getDocumentUserState = async (documentId, userId) => {
  const response = await fetch(documentUserStateEndpoint(documentId, userId), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  });

  if (!response.ok) {
    if (documentUserStateResponseUnauthorized(response)) return null;
    const errorData = await response
      .json()
      .catch(() => ({ errors: ['Failed to fetch document user state'] }));
    registerErrorInRollbar('Failed to fetch document user state', { detail: errorData });
    return null;
  }

  return await response.json();
};

const syncSectionControllersFromKvActions = (actions, application) => {
  if (!application) throw new Error('Stimulus application instance is required');

  // Now update UI for each affected node
  actions.forEach(({ arguments: { uid, key, value } }) => {
    const node = document.querySelector(`[data-uid="${normalizeNodeUid(uid)}"]`);
    if (!node) return;

    const sectionController = application.getControllerForElementAndIdentifier(
      node,
      'case-experience--section-collapse',
    );
    if (sectionController) {
      sectionController.stateValue = { ...sectionController.stateValue, [key]: value };
      sectionController.updateUI();
    }

    // Update button-action controller if present
    const buttonController = application.getControllerForElementAndIdentifier(
      node,
      'case-experience--button-action',
    );
    if (buttonController) {
      if (key === 'invisible') {
        node.setAttribute('data-case-experience--button-action-invisible-value', value ? 'true' : 'false');
      }
      if (key === 'inactive') {
        node.setAttribute('data-case-experience--button-action-inactive-value', value);
      }
    }
  });
};

const normalizeActions = (actions) => {
  return actions.map((action) => {
    const nodeUid = normalizeNodeUid(action.arguments?.uid);
    if (action.action === 'setNodeInactive' && nodeUid) {
      return {
        action: 'setNodeKVPair',
        arguments: { uid: nodeUid, key: 'inactive', value: action.arguments.value },
      };
    } else if (action.action === 'setNodeInvisible' && nodeUid) {
      return {
        action: 'setNodeKVPair',
        arguments: {
          uid: nodeUid,
          key: 'invisible',
          value: action.arguments.value,
        },
      };
    }
    return action;
  });
};

/** True when authored KV/onClick actions need apply_state (Turbo, visibility, progression). */
const requiresAuthoritativeApplyState = (actions) => {
  const normalized = normalizeActions(Array.isArray(actions) ? actions : []);
  if (normalized.length === 0) return false;

  return normalized.some((action) => {
    if (action.action !== 'setNodeKVPair') {
      return true;
    }
    return action.arguments?.key !== 'inactive';
  });
};

const mergePassiveInactiveKvIntoState = (state, actions) => {
  const normalized = normalizeActions(Array.isArray(actions) ? actions : []);
  if (requiresAuthoritativeApplyState(normalized)) return state;

  const updatedAt = Date.now();
  normalized
    .filter((action) => action.action === 'setNodeKVPair' && action.arguments?.key === 'inactive')
    .forEach(({ arguments: { uid, value } }) => {
      if (!state[uid]) state[uid] = {};
      state[uid].inactive = { value, updatedAt };
    });

  return state;
};

const batchSetNodeKVPair = async (actions, documentId, userId, application) => {
  // Group by documentId/userId, then by nodeUid/key
  const state = {};

  actions = filterAndFlattenActions(actions);

  actions.forEach(({ arguments: { uid, key, value } }) => {
    if (!state[uid]) state[uid] = {};

    state[uid][key] = { value, updatedAt: Date.now() };
  });

  if (!documentId || !userId || Object.keys(state).length === 0) return;

  const response = await applyCaseExperienceState({
    documentId,
    userId,
    body: { document_user_state: { state } },
  });
  if (response && application && !response.turbo_streams) {
    syncSectionControllersFromKvActions(actions, application);
  }
  return response;
};

const submitFeedback = async (threadId, rating, issues, comment) => {
  const response = await fetch(`/ai_assistant/threads/${threadId}/feedbacks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({
      feedback: {
        smiley_rating: rating,
        issues,
        comment,
      },
    }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ errors: ['Failed to submit feedback'] }));
    registerErrorInRollbar('AI Assistant feedback submission failed', {
      threadId,
      rating,
      issues,
      comment,
      status: response.status,
      errorResponse: errorData,
    });
    throw new Error(errorData.errors?.[0] || 'Failed to submit feedback');
  }

  return response;
};

const continueToNextSectionLegacy = async ({
  element,
  documentId,
  userId,
  application,
  createContinueButtonIfNeeded = false,
}) => {
  if (!element) return;
  const currentSection = element.closest('.doc-section');
  if (!currentSection) return;

  const nextUidFromButtonOnClick = (() => {
    try {
      const raw = element.getAttribute('data-case-experience--button-action-on-click-value');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      for (const action of parsed) {
        const args = action?.arguments;
        if (!args || typeof args !== 'object') continue;
        const key = `${args.key || ''}`;
        const value = Object.prototype.hasOwnProperty.call(args, 'value') ? args.value : undefined;
        const uid = args.uid;
        if ((key === 'invisible' || key === 'collapsed') && value === false && uid) {
          return normalizeNodeUid(uid);
        }
      }
      return null;
    } catch {
      return null;
    }
  })();

  const actions = [
    {
      uid: currentSection.dataset.uid,
      action: 'setNodeKVPair',
      arguments: { key: 'collapsed', uid: currentSection.dataset.uid, value: true },
    },
  ];

  const casePathContext = getCasePathContextFromDom();
  const nextSection = nextUidFromButtonOnClick
    ? document.querySelector(`.doc-section[data-uid="${nextUidFromButtonOnClick}"]`)
    : getNextVisibleDocSection(currentSection, casePathContext);

  const nextSectionUidForActions = nextSection?.classList?.contains?.('doc-section')
    ? nextSection.dataset.uid
    : nextUidFromButtonOnClick;

  if (nextSectionUidForActions) {
    actions.push(
      {
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: nextSectionUidForActions, value: false },
      },
      {
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'collapsed', uid: nextSectionUidForActions, value: false },
      },
    );
  }
  if (createContinueButtonIfNeeded) {
    actions.push(createNewContinueButtonInSection(nextSection, documentId, userId));
  }

  const updatedActions = filterAndFlattenActions(actions);
  return batchSetNodeKVPair(updatedActions, documentId, userId, application);
};

const dispatchCaseExperienceRefreshContinueButtonsIfTwoCol = () => {
  if (typeof document === 'undefined') return;
  const raw = document.getElementById('document-container')?.getAttribute('data-case-experience--case-viewer-document-id-value');
  const documentId = raw != null && raw !== '' ? Number(raw) : null;
  document.dispatchEvent(
    new CustomEvent('case-experience:refresh-continue-buttons', {
      bubbles: true,
      detail: { documentId: Number.isFinite(documentId) ? documentId : null },
    }),
  );
};

const percentRecommendedTimeFromDomTimeFields = () => {
  const timeField = document.getElementById('timeOnCaseField');
  const recommendedTimeElement = document.querySelector('#recommended-time-seconds');
  const timeOnCase = parseFloat(String(timeField?.value ?? '').trim());
  const recommendedTime = recommendedTimeElement
    ? parseInt(recommendedTimeElement.dataset.value || '0', 10)
    : 0;
  if (!Number.isFinite(timeOnCase) || recommendedTime <= 0) return null;
  return Math.ceil((timeOnCase / 1000 / recommendedTime) * 100);
};

const finalizeCaseExperienceUnlockFromResponsePayload = (payload) => {
  if (!payload?.state || typeof payload.state !== 'object') return;
  const mergedState = mergeDocumentUserStateForContinueSync(getDusFromDom(), payload.state);
  updateDusInDom(mergedState);
  syncAllContinueButtonsForDocument(mergedState);
  dispatchCaseExperienceRefreshContinueButtonsIfTwoCol();
};

const pauseVideosInClosestDocSection = (element) => {
  const section = element?.closest('.doc-section');
  if (!section) return;
  section.querySelectorAll('video').forEach((video) => {
    try {
      video.pause();
    } catch {
      // Ignore (e.g. media not loaded)
    }
  });
};

const caseExperienceOpenedCardTitleEl = () => {
  const root =
    document.getElementById('case-experience-active-pane') ||
    document.getElementById('case-experience-content') ||
    document.getElementById('document-container');
  if (!root) return null;
  return (
    root.querySelector('.doc-section-header') ||
    root.querySelector('#whats-next-card-heading') ||
    root.querySelector('[data-level="1"]') ||
    null
  );
};

const caseExperienceCurrentNavButton = () => {
  const nav = document.getElementById('case-experience-section-nav');
  if (!nav) return null;
  return (
    nav.querySelector('button[aria-current="step"]') ||
    nav.querySelector('button[aria-current]') ||
    null
  );
};

const isVisibleScrollTarget = (el) => {
  if (!el || typeof window === 'undefined') return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
};

const caseExperienceWindowScrollBehavior = () => {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  ) {
    return 'auto';
  }
  return 'smooth';
};

/** Keep the current Program list item in view after Turbo replaces the nav (which resets sidebar scroll). */
const scrollCaseExperienceSidebarToCurrentSection = () => {
  const sidebar = document.getElementById('mobile-lessons-sidebar');
  const current = caseExperienceCurrentNavButton();
  if (!sidebar || !current) return;

  const sidebarRect = sidebar.getBoundingClientRect();
  const currentRect = current.getBoundingClientRect();
  const delta =
    currentRect.top -
    sidebarRect.top -
    (sidebar.clientHeight / 2 - currentRect.height / 2);
  sidebar.scrollTop += delta;
};

const resetCaseExperienceOpenedCardScrollContainers = () => {
  const contentScroll = document.getElementById('case-experience-content');
  if (contentScroll) {
    // Overflow anchoring can re-apply the previous card's scroll offset after Turbo replaces the pane.
    contentScroll.style.overflowAnchor = 'none';
    contentScroll.scrollTop = 0;
  }
  // Below lg, design_system layout scrolls #main-content (body is overflow-hidden); window.scrollTo is a no-op.
  const mainContent = document.getElementById('main-content');
  if (mainContent) {
    mainContent.style.overflowAnchor = 'none';
    mainContent.scrollTop = 0;
  }
  scrollCaseExperienceSidebarToCurrentSection();
};

const scrollCaseExperienceWindowForOpenedCard = () => {
  const behavior = caseExperienceWindowScrollBehavior();
  const header =
    document.querySelector('[aria-label="Header actions"] [role="tablist"]') ||
    document.querySelector('[aria-label="Header actions"] header') ||
    document.querySelector('[aria-label="Header actions"]');

  if (isVisibleScrollTarget(header)) {
    const y = header.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, y), behavior });
    return;
  }

  // Header actions is `hidden md:block` — on mobile it still matches but has zero box.
  const contentScroll = document.getElementById('case-experience-content');
  if (contentScroll) {
    window.scrollTo({ top: 0, behavior });
    return;
  }

  const titleEl = caseExperienceOpenedCardTitleEl();
  if (!titleEl) return;
  const y = titleEl.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({ top: Math.max(0, y), behavior });
};

/**
 * After opening/switching a card:
 * - reset the content pane to its top (keep padding; do not flush the title)
 * - reset #main-content on mobile (design-system scrollport below lg)
 * - bring the case header (Authors & Editors, etc.) back into view on md+
 * - keep the current Program list item in view (Turbo nav replace resets sidebar scroll)
 */
const scrollOpenedCardTitleIntoView = () => {
  resetCaseExperienceOpenedCardScrollContainers();
  scrollCaseExperienceWindowForOpenedCard();
};

/** Content/sidebar first; window scroll once after double-rAF so Turbo anchoring can settle. */
const scheduleScrollOpenedCardTitleIntoView = () => {
  resetCaseExperienceOpenedCardScrollContainers();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      resetCaseExperienceOpenedCardScrollContainers();
      scrollCaseExperienceWindowForOpenedCard();
    });
  });
};

/**
 * Attempts to continue the case experience to the next section.
 *
 * For the two-column Rails/Turbo layout:
 *   - POSTs JSON to unlock the next section (same pattern as apply_state).
 *   - Merges DUS into the DOM, then applies embedded `turbo_streams` HTML.
 *   - Pauses any playing videos in the just-revealed section.
 *   - Scrolls content pane to top and keeps header/nav in view after transition.
 *
 * For the legacy layout (single-column or fallback), delegates to continueToNextSectionLegacy.
 *
 * @param {Object} opts
 *   - element: The DOM element that initiated the continue (usually a button).
 *   - documentId: The document ID corresponding to the current case.
 *   - createContinueButtonIfNeeded: If true, fallback to legacy path.
 * @returns {Promise<void>}
 */
const syncTwoColCollapseStateForActiveRoot = async (documentId, userId, activeSectionUid) => {
  const nav = document.getElementById('case-experience-section-nav');
  const rootUids = nav
    ? [...nav.querySelectorAll('li[data-root-section-uid]')].map((li) =>
        normalizeNodeUid(li.dataset.rootSectionUid),
      )
    : [];
  if (!rootUids.length || !documentId || !userId) return false;

  const normalizedActive = normalizeNodeUid(activeSectionUid);
  const updatedAt = Date.now();
  const state = {};
  rootUids.forEach((uid) => {
    state[uid] = {
      collapsed: { value: uid !== normalizedActive, updatedAt },
    };
  });

  try {
    await patchDocumentUserState({
      documentId,
      userId,
      body: {
        document_user_state: { state },
      },
    });
    return true;
  } catch {
    return false;
  }
};

// Unlock through the section router: the network when it works, the cache when it does not.
const postUnlockNextSection = async (documentId, userId, currentSectionUid, continueButtonUid) => {
  return routeCaseUnlock({
    documentId,
    userId,
    currentSectionUid,
    continueButtonUid,
    post: async () => {
      const unlockUrl = caseExperienceUnlockUrl(documentId);
      try {
        return await fetch(unlockUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify(
            withCaseExperienceAdminPreviewMode({
              current_section_uid: currentSectionUid,
            }),
          ),
        });
      } catch (error) {
        return { ok: false, networkError: error };
      }
    },
  });
};

const continueToNextSection = async (opts) => {
  const { createContinueButtonIfNeeded } = opts;

  // If we are in the two-column Turbo section streaming case...
  if (
    !createContinueButtonIfNeeded &&      // Don't use legacy/fallback continue logic
    opts.element &&                      // A DOM context is required (the button, or other triggering element)
    opts.documentId                      // Document ID must be present for API call
  ) {
    // Attempt to find the section for which the continue button was clicked.
    const currentSectionUid = resolveUnlockCurrentSectionUid(
      opts.element,
      opts.currentSectionUidOverride,
    );
    if (currentSectionUid) {
      const continueButtonUid = opts.element.closest('[data-uid]')?.getAttribute('data-uid');
      let res = await postUnlockNextSection(opts.documentId, opts.userId, currentSectionUid, continueButtonUid);

      if (res.networkError) throw new Error('network_error');

      if (!res.ok) {
        if (opts.currentSectionUidOverride) {
          return null;
        }
        if (railsStudentCaseUnlockContinue()) {
          const errorData = await res.json().catch(() => ({}));
          if (errorData.error_code === 'not_active_section') {
            const synced = await syncTwoColCollapseStateForActiveRoot(
              opts.documentId,
              opts.userId,
              currentSectionUid,
            );
            if (synced) {
              res = await postUnlockNextSection(opts.documentId, opts.userId, currentSectionUid, continueButtonUid);
              if (res.networkError) throw new Error('network_error');
            }
          }
          if (!res.ok) {
            const retryErrorData = await res.json().catch(() => errorData);
            throw new Error(
              retryErrorData.error_code || retryErrorData.error || 'Failed to unlock next section',
            );
          }
        } else {
          return continueToNextSectionLegacy(opts);
        }
      }

      const responseData = await res.json();
      pauseVideosInClosestDocSection(opts.element);
      const hasTurboStreams =
        typeof responseData?.turbo_streams === 'string' && responseData.turbo_streams.trim().length > 0;
      const finalizeUnlockFromResponse = () => {
        finalizeCaseExperienceUnlockFromResponsePayload(responseData);
      };
      applyCaseExperienceTurboStreamsFromPayload(responseData);
      if (hasTurboStreams && typeof document !== 'undefined') {
        const onAfterUnlockStream = () => {
          document.removeEventListener('turbo:after-stream-render', onAfterUnlockStream);
          finalizeUnlockFromResponse();
          // Nav replace can reset sidebar after the initial schedule; re-center current item.
          scrollCaseExperienceSidebarToCurrentSection();
        };
        document.addEventListener('turbo:after-stream-render', onAfterUnlockStream);
      } else {
        finalizeUnlockFromResponse();
      }
      // Also schedule immediately: unlock streams often do not emit turbo:after-stream-render
      // via renderStreamMessage, and overflow anchoring can restore the previous card offset.
      scheduleScrollOpenedCardTitleIntoView();
      return responseData;
    }
  }

  // Default: invoke the older single-column/React continue behavior.
  return continueToNextSectionLegacy(opts);
};

const continueButtonUidForSection = (section) => {
  if (!section?.dataset?.uid) return null;
  return section.dataset.continueButtonUid || `continue_button_${section.dataset.uid}`;
};

const continueButtonConsumedInDus = (section, dus) => {
  const uid = continueButtonUidForSection(section);
  if (!uid || !dus) return false;
  return dus[uid]?.invisible?.value === true;
};

const sectionHasContinueButtonInDom = (section) => {
  if (!section) return false;
  if (section.querySelector('[data-case-experience--case-viewer-target="continueButton"]')) return true;
  if (
    section.querySelector(
      '.doc-button-slice.doc-rails-button[data-controller="case-experience--button-action"][data-case-experience--button-action-name-value="continue"]',
    )
  ) {
    return true;
  }
  const slices = section.querySelectorAll('.doc-button-slice.doc-rails-button');
  for (const slice of slices) {
    const inner = slice.querySelector('button');
    if (inner && inner.textContent.trim().toUpperCase() === 'CONTINUE') return true;
  }
  return false;
};

const sectionHasContinueOrEquivalent = (section) => sectionHasContinueButtonInDom(section);

const ensureSectionContinueHasCaseViewerTarget = (section) => {
  if (!section || section.querySelector('[data-case-experience--case-viewer-target="continueButton"]')) return;
  const byName = section.querySelector(
    '.doc-button-slice.doc-rails-button[data-controller="case-experience--button-action"][data-case-experience--button-action-name-value="continue"]',
  );
  if (byName) {
    byName.setAttribute('data-case-experience--case-viewer-target', 'continueButton');
    return;
  }
  const slices = section.querySelectorAll('.doc-button-slice.doc-rails-button');
  for (const slice of slices) {
    const inner = slice.querySelector('button');
    if (inner && inner.textContent.trim().toUpperCase() === 'CONTINUE') {
      slice.setAttribute('data-case-experience--case-viewer-target', 'continueButton');
      return;
    }
  }
};

const PRE_CONTINUE_DIVIDER_HTML =
  '<hr class="aq-divider aq-divider--horizontal m-0 w-full case-experience-pre-continue-divider" aria-hidden="true">';

const createNewContinueButtonInSection = (section, documentId, userId) => {
  if (!section) return [];

  ensureSectionContinueHasCaseViewerTarget(section);

  const actions = [];
  const continueButtonId = continueButtonUidForSection(section);
  const hasEquiv = sectionHasContinueOrEquivalent(section);
  if (!hasEquiv) {
    if (continueButtonConsumedInDus(section, getDusFromDom())) {
      return actions;
    }
    const vpeFeedbackContinuePresent = !!section.querySelector(
      '[data-case-experience--ai-assistant-feedback-target="continueButton"]',
    );
    const feedbackRoot = section.querySelector('[data-controller~="case-experience--ai-assistant-feedback"]');
    const vpeFeedbackModalSubmitted =
      feedbackRoot?.getAttribute('data-case-experience--ai-assistant-feedback-feedback-submitted-value') === 'true';
    const aiChatPresent = !!section.querySelector('[data-controller~="case-experience--ai-assistant-chat"]');
    const sectionHasAiChildren =
      section.hasAttribute?.('data-has-ai-children') ||
      section.dataset?.hasAiChildren === 'true';
    const skipSyntheticWhileVpeCardActive =
      sectionHasAiChildren &&
      (vpeFeedbackContinuePresent ||
        (!vpeFeedbackModalSubmitted && (!!feedbackRoot || aiChatPresent)));
    if (skipSyntheticWhileVpeCardActive) {
      return actions;
    }
    // Add button beforeend inside next section -> .doc-section-body > .doc-children element
    let inactive = 1;
    const docChildren = section?.querySelector('.doc-section-body .doc-children');
    if (!docChildren) {
      return actions;
    }

    const buttonsInSection = section.querySelectorAll('button');
    for (let i = 0; i < buttonsInSection.length; i++) {
      const btn = buttonsInSection[i];
      if (btn.textContent.trim().toLowerCase() === 'submit') {
        inactive = 2;
      }
    }

    if (railsStudentCaseUnlockContinue()) {
      const buttonHtml = `
        <div
          id="${continueButtonId}"
          data-is-node
          data-uid="${continueButtonId}"
          class="doc-button-slice doc-rails-button ${continueButtonId}"
          data-controller="case-experience--button-action"
          data-case-experience--case-viewer-target="continueButton"
          data-case-experience--button-action-uid-value="${continueButtonId}"
          data-case-experience--button-action-name-value="continue"
          data-case-experience--button-action-document-id-value="${documentId}"
          data-case-experience--button-action-user-id-value="${userId}"
          data-case-experience--button-action-on-click-value='[]'
          data-case-experience--button-action-invisible-value="false"
          data-case-experience--button-action-inactive-value="${inactive}"
        >
          ${renderCaseExperienceAquiButtonHtml({ label: 'Continue' })}
        </div>
      `;
      docChildren.insertAdjacentHTML('beforeend', PRE_CONTINUE_DIVIDER_HTML + buttonHtml);
      return [];
    }

    const continueButtonActions = [];
    continueButtonActions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'collapsed', uid: section.dataset.uid, value: true },
    });

    continueButtonActions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'invisible', uid: continueButtonId, value: true },
    });

    continueButtonActions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'inactive', uid: continueButtonId, value: 2 },
    });

    const nextSection = getNextVisibleDocSection(section, getCasePathContextFromDom());
    const nextUidFromDataset = nextSection?.dataset?.uid || section.dataset?.caseNextRootSectionUid;
    if (nextSection) {
      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: nextSection.dataset.uid, value: false },
      });
      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'collapsed', uid: nextSection.dataset.uid, value: false },
      });
    } else if (nextUidFromDataset) {
      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: nextUidFromDataset, value: false },
      });
      continueButtonActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'collapsed', uid: nextUidFromDataset, value: false },
      });
    }

    const buttonHtml = `
        <div
          id="${continueButtonId}"
          data-is-node
          data-uid="${continueButtonId}"
          class="doc-button-slice doc-rails-button ${continueButtonId}"
          data-controller="case-experience--button-action"
          data-case-experience--case-viewer-target="continueButton"
          data-case-experience--button-action-uid-value="${continueButtonId}"
          data-case-experience--button-action-name-value="continue"
          data-case-experience--button-action-document-id-value="${documentId}"
          data-case-experience--button-action-user-id-value="${userId}"
          data-case-experience--button-action-on-click-value='${JSON.stringify(continueButtonActions)}'
          data-case-experience--button-action-invisible-value="false"
          data-case-experience--button-action-inactive-value="${inactive}"
        >
          ${renderCaseExperienceAquiButtonHtml({ label: 'Continue' })}
        </div>
      `;
    docChildren.insertAdjacentHTML('beforeend', PRE_CONTINUE_DIVIDER_HTML + buttonHtml);
  }

  return actions;
};

const currentSection = (element) => {
  return element.closest('.doc-section');
};

const rootCardSection = (element) => {
  if (!element) return null;
  return (
    element.closest('[data-case-experience--case-viewer-target="section"]') ||
    element.closest('.doc-section[data-level="1"]') ||
    currentSection(element)
  );
};

const activePaneRootSectionElement = () => {
  const pane = document.getElementById('case-experience-active-pane');
  if (!pane) return null;
  return (
    pane.querySelector('[data-case-experience--case-viewer-target="section"]') ||
    pane.querySelector('.doc-section[data-level="1"]')
  );
};

const resolveUnlockCurrentSectionUid = (element, override) => {
  if (override != null && override !== '') {
    return normalizeNodeUid(override);
  }
  const paneRoot = activePaneRootSectionElement();
  const paneUid = paneRoot?.dataset?.uid;
  if (paneUid) return normalizeNodeUid(paneUid);
  const section = rootCardSection(element);
  const uid = section?.dataset?.uid;
  return uid ? normalizeNodeUid(uid) : null;
};

const waitForVisible = (el, cb) => {
  const isVisible = (elem) => {
    if (!elem) return false;
    const style = window.getComputedStyle(elem);
    return style.display !== 'none' && style.visibility !== 'hidden' && elem.offsetParent !== null;
  };
  if (isVisible(el)) {
    cb();
  } else {
    const observer = new MutationObserver(() => {
      if (isVisible(el)) {
        observer.disconnect();
        cb();
      }
    });
    observer.observe(document.body, { attributes: true, childList: true, subtree: true });
  }
};

// Restore the original on-click value for the continue button in the previous section
const restoreOriginalContinueButtonOnClick = (previousSection, actions) => {
  const updatedActions = [...actions];
  if (previousSection && previousSection.classList.contains('doc-section')) {
    const continueButtonContainer = previousSection.querySelector(
      '[data-case-experience--case-viewer-target="continueButton"]',
    );
    if (continueButtonContainer) {
      const originalOnClick = continueButtonContainer.getAttribute(
        'data-original-button-action-on-click-value',
      );
      if (originalOnClick) {
        continueButtonContainer.setAttribute(
          'data-case-experience--button-action-on-click-value',
          originalOnClick,
        );
      }
      updatedActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: continueButtonContainer.dataset.uid, value: false },
      });
      updatedActions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'inactive', uid: continueButtonContainer.dataset.uid, value: 1 },
      });
    }
  }

  return updatedActions;
};

/** Two-column pane: sibling walk has no earlier .doc-section; mirror single-column fallback using nav root order */
const appendTwoColumnAiFallbackNavBackwardActions = (actions, rawCurrentUid) => {
  const nav = document.getElementById('case-experience-section-nav');
  if (!nav || !rawCurrentUid) return;
  const roots = [...nav.querySelectorAll('li[data-root-section-uid]')].map((li) => ({
    uid: normalizeNodeUid(li.dataset.rootSectionUid),
    aiFlag: String(li.dataset.rootAiFlag || '').trim(),
  }));
  const cur = normalizeNodeUid(rawCurrentUid);
  const idx = roots.findIndex((r) => r.uid === cur);
  if (idx <= 0) return;
  let j = idx - 1;
  while (j >= 0 && roots[j].aiFlag === 'aiOn') {
    const uid = roots[j].uid;
    actions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'collapsed', uid: uid, value: true },
    });
    actions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'invisible', uid: uid, value: true },
    });
    j -= 1;
  }
};

const showAiErrorModal = async ({
  buttonId = 'vpe-chat-error-continue-button',
  documentId,
  application,
  modalId = 'dynamic-vpe-chat-error-modal',
  targetController = 'ai-assistant-chat',
  fallbackModalRoot,
} = {}) => {
  const modalRoot = await fetchAndMountVpeErrorModal({
    documentId,
    variant: 'chat',
    modalId,
    targetController,
    continueButtonId: buttonId,
    application,
    fallbackModalRoot,
  });

  return Boolean(modalRoot);
};

// Show the AI VPE feedback error modal
const showAiVpeFeedbackErrorModal = async ({
  buttonId = 'vpe-feedback-error-continue-button',
  documentId,
  application,
  modalId = 'dynamic-vpe-feedback-error-modal',
  targetController = 'ai-assistant-feedback',
  fallbackModalRoot,
} = {}) => {
  const modalRoot = await fetchAndMountVpeErrorModal({
    documentId,
    variant: 'feedback',
    modalId,
    targetController,
    continueButtonId: buttonId,
    application,
    fallbackModalRoot,
  });

  return Boolean(modalRoot);
};

// Shared fallback/restore logic for AI assistant error handling
// Hides the current section, traverses to the first previous section not aiOn, restores continue button, applies actions, and hides error modal
const handleAiAssistantFallback = async ({
  currentSectionElement,
  documentId,
  userId,
  application,
  batchSetNodeKVPairFn = batchSetNodeKVPair,
  restoreContinueButtonFn = restoreOriginalContinueButtonOnClick,
  executeActionsInDomFn = syncSectionControllersFromKvActions,
  onAfterFallback = null,
}) => {
  if (!currentSectionElement) return;

  const setAiFallbackFlagsInDom = () => {
    const documentContainer = document.querySelector('#document-container');
    if (documentContainer) {
      documentContainer.setAttribute('data-case-experience--case-viewer-ai-fallback-cards-value', 'true');
    }
    document
      .querySelectorAll('[data-case-experience--section-collapse-ai-fallback-cards-value]')
      .forEach((el) => {
        el.setAttribute('data-case-experience--section-collapse-ai-fallback-cards-value', 'true');
      });
  };

  const previousSiblingDocSection = (el) => {
    if (!el) return null;
    let p = el.previousElementSibling;
    while (p && !p.classList?.contains('doc-section')) {
      p = p.previousElementSibling;
    }
    return p;
  };

  const actions = [];
  actions.push({
    uid: generateUUID(),
    action: 'setNodeKVPair',
    arguments: { key: 'aiFallbackCards', uid: 'root', value: true },
  });

  actions.push({
    uid: generateUUID(),
    action: 'setNodeKVPair',
    arguments: { key: 'collapsed', uid: currentSectionElement.dataset.uid, value: true },
  });
  actions.push({
    uid: generateUUID(),
    action: 'setNodeKVPair',
    arguments: { key: 'invisible', uid: currentSectionElement.dataset.uid, value: true },
  });

  let previousSection = previousSiblingDocSection(currentSectionElement);
  const twoColNavBackwalk = previousSection === null;
  let prevAiOnSection = null;
  if (twoColNavBackwalk) {
    appendTwoColumnAiFallbackNavBackwardActions(actions, currentSectionElement.dataset.uid);
  } else {
    while (previousSection && previousSection.getAttribute('data-ai-display-flag') === 'aiOn') {
      // Remember the last aiOn section we collapse/hide so we can restore its continue button
      if (!prevAiOnSection) {
        prevAiOnSection = previousSection;
      }
      actions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'collapsed', uid: previousSection.dataset.uid, value: true },
      });
      actions.push({
        uid: generateUUID(),
        action: 'setNodeKVPair',
        arguments: { key: 'invisible', uid: previousSection.dataset.uid, value: true },
      });
      previousSection = previousSiblingDocSection(previousSection);
    }

    while (previousSection && previousSection.getAttribute('data-ai-display-flag') === 'aiOff') {
      previousSection = previousSiblingDocSection(previousSection);
    }
  }

  // At this point, previousSection is the previous *non-AI* section (or null).
  // prevAiOnSection is the immediately previous aiOn section, if any; that's the one whose continue btn needs making visible

  // Reveal the landing (non-AI) section itself when found via sibling walk
  if (previousSection && !twoColNavBackwalk) {
    actions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'invisible', uid: previousSection.dataset.uid, value: false },
    });
    actions.push({
      uid: generateUUID(),
      action: 'setNodeKVPair',
      arguments: { key: 'collapsed', uid: previousSection.dataset.uid, value: false },
    });
  }

  let actionsWithContinueRestored;

  // If we have a prevAiOnSection, restore its continue button before new actions (so it can be traversed)
  if (prevAiOnSection) {
    actionsWithContinueRestored = restoreContinueButtonFn(prevAiOnSection, actions);
  } else {
    actionsWithContinueRestored = restoreContinueButtonFn(previousSection, actions);
  }

  let response;
  let persistFailed = false;
  try {
    response = await batchSetNodeKVPairFn(actionsWithContinueRestored, documentId, userId, application);
  } catch {
    persistFailed = true;
  }

  if (twoColNavBackwalk && response) {
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    const pane = document.querySelector('#case-experience-active-pane .doc-section[data-uid]');
    const revealActions = restoreContinueButtonFn(pane, []);
    if (Array.isArray(revealActions) && revealActions.some((x) => x?.action === 'setNodeKVPair')) {
      await batchSetNodeKVPairFn(revealActions, documentId, userId, application);
    }
  }

  if (persistFailed || (response && !response.turbo_streams)) {
    // Legacy/no-stream fallback or offline: keep local DOM flags in sync.
    setAiFallbackFlagsInDom();
    refreshCaseNextRootSectionUidsInDom();
    executeActionsInDomFn(actionsWithContinueRestored, application);
  }
  requestAnimationFrame(() => {
    // Hide the error modal after fallback actions (both turbo and no-turbo paths).
    removeDynamicVpeErrorModals();
    refreshCaseNextRootSectionUidsInDom();
    if (typeof onAfterFallback === 'function') onAfterFallback();
  });
};

// Convert simple markdown to HTML (headings, bold, line breaks)
const convertSimpleMarkdown = (md) => {
  if (!md) return '';
  let html = md;
  // Headings: #, ##, ###, ####, #####
  html = html.replace(/^##### (.*)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#### (.*)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.*)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*)$/gm, '<h1>$1</h1>');
  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  // Links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^\)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  // Unordered lists (lines starting with - or *)
  html = html.replace(/^(\s*)[-*] (.*)$/gm, '$1<li>$2</li>');
  // Wrap consecutive <li> in <ul>
  html = html.replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul>${match.replace(/\s*$/, '')}</ul>`);
  // Line breaks: remove newlines immediately after block tags, then convert remaining to <br>
  html = html.replace(/(<\/h[1-5]>|<\/ul>|<\/ol>)\s*\n/g, '$1');
  html = html.replace(/\n/g, '<br>');

  return html;
};

const getDusFromDom = () => {
  const documentContainer = document.getElementById('document-container');
  if (!documentContainer) return {};
  const dusData = documentContainer.getAttribute('data-case-experience--case-viewer-document-user-state-value');
  if (!dusData) return {};
  try {
    return JSON.parse(dusData);
  } catch (e) {
    console.error('Failed to parse document user state JSON:', dusData, e);
    registerErrorInRollbar('Failed to parse document user state JSON', {
      rawValue: dusData,
      error: e,
    });
    return {};
  }
};

const vpeChatErrorBlocksFeedback = (dus) => {
  const root = dus?.root;
  if (!root || typeof root !== 'object') return false;
  if (root.error?.value === 'aiAssistantChatError') return true;
  if (root.aiFallbackCards?.value === true) return true;
  return false;
};

const QUESTION_ROOT_SELECTOR = [
  '.doc-multiple-choice-wrapper[data-controller~="case-experience--multiple-choice"]',
  '.doc-rails-text-input[data-controller~="case-experience--notes"]',
  '.doc-category-matcher[data-controller~="case-experience--category-matcher"]',
  '[data-controller~="case-experience--rating-bar"]',
].join(', ');

const getDusEntryForUid = (state, uid) => {
  if (!state || uid == null) return null;
  const n = normalizeNodeUid(uid);
  if (state[n]) return state[n];
  const spaced = n.split('-').join(' ');
  if (state[spaced]) return state[spaced];
  const hyphenated = n.split(' ').join('-');
  if (state[hyphenated]) return state[hyphenated];
  if (n.startsWith('_ws_') && state['']) return state[''];
  return null;
};

const isQuestionKindSubmitted = (entry, kind) => {
  if (!entry) return false;
  if (kind === 'rating') {
    const v = entry.value;
    if (v && typeof v === 'object' && 'value' in v) {
      return Number(v.value) > 0;
    }
    return Number(v) > 0;
  }
  const gradedDone = !!(entry.graded && entry.graded.value === true);
  if (!gradedDone) return false;
  const aiFeedbackReceived =
    entry.aiSummaryStatementResponseRecieved?.value === true ||
    !!(entry.aiSummaryStatementFeedback?.value &&
      String(entry.aiSummaryStatementFeedback.value).trim().length > 0);
  if (aiFeedbackReceived) {
    return entry.aiSummaryStatementUserRatingSubmitted?.value === true;
  }
  return true;
};

const collectCardQuestionDescriptors = (cardSection) => {
  if (!cardSection) return [];
  const seen = new Set();
  const out = [];
  cardSection.querySelectorAll(QUESTION_ROOT_SELECTOR).forEach((el) => {
    const controllers = (el.getAttribute('data-controller') || '').split(/\s+/).filter(Boolean);
    let uid = null;
    let kind = 'graded';
    if (controllers.includes('case-experience--multiple-choice')) {
      uid = el.getAttribute('data-case-experience--multiple-choice-node-uid-value');
      if (uid && !el.querySelector('.doc-multiple-choice-answer, [data-case-experience--multiple-choice-target="answer"]')) {
        return;
      }
    } else if (controllers.includes('case-experience--notes')) {
      uid = el.getAttribute('data-case-experience--notes-node-uid-value');
    } else if (controllers.includes('case-experience--category-matcher')) {
      uid = el.getAttribute('data-case-experience--category-matcher-node-uid-value');
    } else if (controllers.includes('case-experience--rating-bar')) {
      uid = el.getAttribute('data-case-experience--rating-bar-node-uid-value');
      kind = 'rating';
    }
    if (!uid) return;
    const key = `${normalizeNodeUid(uid)}:${kind}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ uid: normalizeNodeUid(uid), kind });
  });
  return out;
};

const mergeDocumentUserStateForContinueSync = (domState, apiState) => mergeCaseState(domState, apiState);

const refreshContinueButtonController = (continueEl, application) => {
  if (!continueEl || !application) return;
  const buttonController = application.getControllerForElementAndIdentifier(
    continueEl,
    'case-experience--button-action',
  );
  if (!buttonController || typeof buttonController.updateButtonState !== 'function') return;

  const rawInactive = continueEl.getAttribute('data-case-experience--button-action-inactive-value');
  if (rawInactive != null && rawInactive !== '') {
    const inactive = Number(rawInactive);
    if (Number.isFinite(inactive)) {
      buttonController.inactiveValue = inactive;
    }
  }
  buttonController.updateButtonState();
};

const syncContinueButtonForCardSection = (section, application, state) => {
  if (!section) return;
  const cardSection = section.closest('[data-case-experience--case-viewer-target="section"]') || section;
  const continueEl = cardSection.querySelector('[data-case-experience--case-viewer-target="continueButton"]');
  if (!continueEl) return;

  const mergedState =
    state && typeof state === 'object' && Object.keys(state).length > 0 ? state : getDusFromDom();
  const descriptors = collectCardQuestionDescriptors(cardSection);
  if (descriptors.length === 0) return;

  const allSubmitted = descriptors.every(({ uid, kind }) =>
    isQuestionKindSubmitted(getDusEntryForUid(mergedState, uid), kind),
  );
  continueEl.setAttribute('data-case-experience--button-action-inactive-value', allSubmitted ? '1' : '2');
  refreshContinueButtonController(continueEl, application);
};

/**
 * Update the state of all "Continue" buttons in the Rails case experience view.
 *
 * - Finds all DOM elements marking continue buttons via data-case-experience--case-viewer-target="continueButton"
 * - Determines if all relevant questions in a card section are "submitted" (_i.e._, graded, completed, or otherwise done)
 * - Applies the correct "inactive" value (1 = enable continue, 2 = disable) on each continue button element
 * - Refreshes the Turbo/Stimulus button-action controller to update the UI, such as enable/disable state, clickability, etc
 * - Uses the provided state (merged DUS), or fallback to current DUS from DOM if not provided
 *
 * @param {Object} state - (optional) The merged document user state object. If omitted or empty, falls back to DOM.
 */
const syncAllContinueButtonsForDocument = (state) => {
  // Get the global Stimulus application instance (make sure we're in a browser context)
  const application = typeof window !== 'undefined' ? window.Stimulus : null;
  if (!application) return; // No Stimulus/app found, do nothing

  // Use passed-in state if present and has keys; otherwise, read DUS (document user state) from DOM attribute
  const mergedState =
    state && typeof state === 'object' && Object.keys(state).length > 0 ? state : getDusFromDom();

  // Select all continue buttons in the document view
  document.querySelectorAll('[data-case-experience--case-viewer-target="continueButton"]').forEach((continueEl) => {
    // Find the parent card section for this continue button
    const cardSection = continueEl.closest('[data-case-experience--case-viewer-target="section"]');
    if (!cardSection) return; // Defensive: can't find containing section, skip

    // Collect all questions (and their types) belonging to this card section
    const descriptors = collectCardQuestionDescriptors(cardSection);
    if (descriptors.length === 0) return; // No questions in this section, skip

    // For EACH descriptor, check if it's been submitted (uses mergedState)
    // If EVERY question is submitted, allow continuing (inactive-value=1), else disable (inactive-value=2)
    const allSubmitted = descriptors.every(({ uid, kind }) =>
      isQuestionKindSubmitted(getDusEntryForUid(mergedState, uid), kind),
    );
    // Set data attribute to control next-button enable/disable state (used by Stimulus controller)
    continueEl.setAttribute(
      'data-case-experience--button-action-inactive-value',
      allSubmitted ? '1' : '2'
    );
    // Notify and refresh Turbo/Stimulus button controller for this button,
    // so the UI picks up the new "inactive" state (e.g., disables or enables it)
    refreshContinueButtonController(continueEl, application);
  });
};

const feedbackCardRatingsIncomplete = (scopeEl) => {
  if (!scopeEl) return false;
  const ratingBars = [...scopeEl.querySelectorAll('[data-controller~="case-experience--rating-bar"]')];
  if (ratingBars.length === 0) return false;

  return ratingBars.some((el) => {
    const value = parseInt(el.getAttribute('data-case-experience--rating-bar-current-value-value'), 10);
    return Number.isNaN(value) || value <= 0;
  });
};

const updateDusInDom = (dus) => {
  const documentContainer = document.getElementById('document-container');
  if (!documentContainer) return;
  documentContainer.setAttribute('data-case-experience--case-viewer-document-user-state-value', JSON.stringify(dus));
};

const filterActionIfAlreadyExistsInDus = (actions, dus) => {
  const existingDus = dus || {};
  actions = Array.isArray(actions) ? actions : [actions];

  return actions.filter((action) => {
    const dusForNode = existingDus[action.arguments.uid] || {};
    const existingValue = dusForNode[action.arguments.key]?.value;

    if (existingValue !== undefined && existingValue === action.arguments.value) {
      // Skip this action as it already exists in DUS with the same value
      return false;
    }

    return true;
  });
};

const registerErrorInRollbar = (message, detail = {}) => {
  if (
    typeof window !== 'undefined' &&
    window.Rollbar &&
    typeof window.Rollbar.error === 'function'
  ) {
    window.Rollbar.error(message, detail);
  }
};

const coerceFiniteNumber = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

// Extract answer_comment sibling HTML and remove from DOM if it's hidden (not yet revealed to the user). Returns { html, sibling } or null.
const extractHiddenAnswerCommentHTML = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment') &&
      sibling.offsetParent === null // not visible in DOM
    ) {
      const html = sibling.outerHTML;
      sibling.remove();
      return { html, sibling };
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

// Same walk as extractHiddenAnswerCommentHTML but removes the sibling regardless of visibility (e.g. .hidden overridden by doc display mode).
const extractAnswerCommentSiblingHtml = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment')
    ) {
      const html = sibling.outerHTML;
      sibling.remove();
      return { html };
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

const DOC_BODY_DISPLAY_MODES_REQUIRING_ANSWER_COMMENT_IN_DOM = [
  'full',
  'outline',
  'ungated',
];

const DISPLAY_MODES = ['student', ...DOC_BODY_DISPLAY_MODES_REQUIRING_ANSWER_COMMENT_IN_DOM];

const docBodyRequiresAnswerCommentInDom = (element) => {
  const docBody = element.closest('.doc-body');
  if (!docBody) return false;
  return DOC_BODY_DISPLAY_MODES_REQUIRING_ANSWER_COMMENT_IN_DOM.some((cls) =>
    docBody.classList.contains(cls),
  );
};

// Copy answer_comment sibling HTML without removing the node (needed when doc display mode CSS expects the section in the tree).
const snapshotAnswerCommentSiblingHtml = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    if (
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment')
    ) {
      return { html: sibling.outerHTML };
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

const resolveAnswerCommentSectionElement = (summarySectionElement, rawNodeUid) => {
  const parent = summarySectionElement?.parentElement;
  if (!parent) return null;
  const direct = Array.from(parent.children).find(
    (el) => el.classList && el.classList.contains('answer_comment'),
  );
  if (direct) return direct;
  const slotAnsUid = `${normalizeNodeUid(rawNodeUid)}_ANS_COMMENT`;
  const slotEl = document.getElementById(`slot-${slotAnsUid}`);
  if (!slotEl) return null;
  return (
    slotEl.querySelector('.doc-section.answer_comment') || slotEl.querySelector('.answer_comment')
  );
};

// Appends answer_comment HTML after the given element's parent (section's children element) if not already present
const restoreAnswerCommentHTML = (element, answerCommentHtml) => {
  if (
    answerCommentHtml &&
    element.parentElement &&
    !element.parentElement.querySelector('.answer_comment')
  ) {
    element.parentElement.insertAdjacentHTML('beforeend', answerCommentHtml);
    const restored = element.parentElement.querySelector('.answer_comment');
    if (restored) {
      restored.classList.remove('hidden');
    }
  }
};

// Remove the answer comment from the UI (if present)
const removeAnswerCommentFromUI = (element) => {
  if (element.parentElement) {
    const answerComment = Array.from(element.parentElement.children).find(
      (el) => el.classList && el.classList.contains('answer_comment'),
    );
    if (answerComment) {
      answerComment.remove();
    }
  }
};

// Set display: 'block' for all elements with ids in the array
const setDisplayBlockByIds = (ids) => {
  if (!Array.isArray(ids)) return;
  ids.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = 'block';
    }
  });
};

const endOfCaseCompletionRevealedInDus = (dus) => {
  const endOfCaseEntry = dus?.end_of_case_text;
  if (endOfCaseEntry?.invisible?.value === false) return true;

  const finishCard = document.querySelector('.finish_case_card[data-uid]');
  const finishUid = finishCard?.dataset?.uid;
  if (!finishUid) return false;

  const finishEntry = getDusEntryForUid(dus, normalizeNodeUid(finishUid));
  return finishEntry?.invisible?.value === false;
};

const revealEndOfCaseCompletionBannerIfNeeded = () => {
  const banner = document.getElementById('end_of_case_text');
  if (!banner || banner.style.display === 'block') return;

  if (endOfCaseCompletionRevealedInDus(getDusFromDom())) {
    setDisplayBlockByIds(['end_of_case_text']);
  }
};

// Set display: 'none' for all elements with ids in the array
const setDisplayNoneByIds = (ids) => {
  if (!Array.isArray(ids)) return;
  ids.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.style.display = 'none';
    }
  });
};

/**
 * Switch two-col active pane to finish / What's next? section when attrs present.
 * @returns {Promise<boolean|null>} true on success, false on failed switch, null when no switch needed
 */
const switchTwoColPaneToFinishSectionIfNeeded = async () => {
  const dc = document.getElementById('document-container');
  const documentId = dc?.getAttribute('data-case-experience--case-viewer-document-id-value');
  const finishUid = dc?.getAttribute('data-case-experience--case-viewer-finish-section-uid-value');
  if (!documentId || !finishUid) return null;

  try {
    const url = caseExperienceSwitchSectionUrl(Number(documentId));
    const body = new URLSearchParams();
    body.set('current_section_uid', finishUid);
    const adminPreviewMode = getCaseExperienceAdminPreviewMode();
    if (adminPreviewMode) {
      body.set('admin_preview_mode', adminPreviewMode);
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'text/vnd.turbo-stream.html, application/json;q=0.9',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: body.toString(),
    });
    if (!res.ok) return false;
    const text = await res.text();
    if (window.Turbo?.renderStreamMessage && text.trimStart().startsWith('<turbo-stream')) {
      await window.Turbo.renderStreamMessage(text);
    }
    return true;
  } catch {
    return false;
  }
};

const WHATS_NEXT_CARD_HEADING_ID = 'whats-next-card-heading';

const whatsNextHeadingIsInActivePane = (heading) => {
  const activePane = document.getElementById('case-experience-active-pane');
  if (!activePane) return true;
  return activePane.contains(heading);
};

const focusWhatsNextCardHeading = () => {
  const heading = document.getElementById(WHATS_NEXT_CARD_HEADING_ID);
  if (!heading || typeof heading.focus !== 'function') return false;
  if (!whatsNextHeadingIsInActivePane(heading)) return false;

  if (!heading.hasAttribute('tabindex')) {
    heading.setAttribute('tabindex', '-1');
  }
  heading.focus();
  return document.activeElement === heading;
};

// Hide completion modal and switch the 2-col pane to the finish / What's next? section
const showFinishCardContentAndSection = async () => {
  hideCaseCompletionModal();
  const paneSwitched = await switchTwoColPaneToFinishSectionIfNeeded();
  if (paneSwitched === false) return;
  focusWhatsNextCardHeading();
};

// Submits selected course IDs as student-choice cases for a document
const submitSelectedStudentChoiceCourses = async (documentId, courseIds) => {
  if (!documentId || !Array.isArray(courseIds) || courseIds.length === 0) return;

  const csrfToken = getCsrfToken();
  const response = await fetch(`/documents/${documentId}/add_as_student_choice_cases`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: JSON.stringify({ courseIds }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { errors: ['Failed to add as student-choice case'] };
    }
    registerErrorInRollbar('Failed to add as student-choice case', { detail: errorData });
    throw new Error(errorData.errors?.[0] || 'Failed to add as student-choice case');
  }

  return response.json();
};

const noteSaveEpochsSettled = (detail) => {
  const start = detail?.saveStartEpoch;
  const end = detail?.saveEndEpoch;
  return start === undefined || start === end;
};

const shouldPreserveAnswerCommentInDom = () => {
  const el = document.getElementById('document-container');
  return el?.getAttribute('data-case-experience--case-viewer-preserve-answer-comment-dom-value') === 'true';
};

/** True if stored HTML present or parent has another child that is `.doc-section.answer_comment` (any visibility). */
const hasAnswerCommentAvailable = (element, storedAnswerCommentHtml) => {
  if (storedAnswerCommentHtml?.length) {
    return true;
  }
  let sibling = element.parentElement?.firstElementChild;
  while (sibling) {
    if (
      sibling !== element &&
      sibling.classList &&
      sibling.classList.contains('doc-section') &&
      sibling.classList.contains('answer_comment')
    ) {
      return true;
    }
    sibling = sibling.nextElementSibling;
  }
  return false;
};

/**
 * After Turbo replaces `case-experience-sticky-bar`, fresh `#panel-sticky` loses
 * `smaller-sticky-info` that side_navigation_page_controller applied when the nav
 * is collapsed. Re-align from the primary side nav panel (unchanged by that stream).
 */
const syncPanelStickyWithSideNavLayout = () => {
  const panelSticky = document.getElementById('panel-sticky');
  if (!panelSticky) return;

  const mainSideNav =
    document.getElementById('side-nav-div-main') ||
    document.querySelector('[data-side-nav-panel]');
  if (!mainSideNav) return;

  const sideNavCollapsed = mainSideNav.classList.contains('collapsed');

  panelSticky.classList.toggle('smaller-sticky-info', sideNavCollapsed);
};

export {
  patchDocumentUserState,
  applyCaseExperienceState,
  updateDocumentUserState,
  getDocumentUserState,
  getCsrfToken,
  syncSectionControllersFromKvActions,
  normalizeActions,
  requiresAuthoritativeApplyState,
  mergePassiveInactiveKvIntoState,
  batchSetNodeKVPair,
  submitFeedback,
  continueToNextSection,
  getNextVisibleDocSection,
  refreshCaseNextRootSectionUidsInDom,
  getCasePathContextFromDom,
  rootSectionVisibleOnCasePath,
  currentSection,
  waitForVisible,
  restoreOriginalContinueButtonOnClick,
  showAiErrorModal,
  showAiVpeFeedbackErrorModal,
  handleAiAssistantFallback,
  createNewContinueButtonInSection,
  continueButtonUidForSection,
  continueButtonConsumedInDus,
  isPassiveRootCardSection,
  PASSIVE_ROOT_CARD_CLASSES,
  sectionHasContinueOrEquivalent,
  AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT,
  convertSimpleMarkdown,
  getDusFromDom,
  mergeDocumentUserStateForContinueSync,
  vpeChatErrorBlocksFeedback,
  syncContinueButtonForCardSection,
  syncAllContinueButtonsForDocument,
  filterActionIfAlreadyExistsInDus,
  updateDusInDom,
  registerErrorInRollbar,
  percentRecommendedTimeFromDomTimeFields,
  applyCaseExperienceTurboStreamsFromPayload,
  extractHiddenAnswerCommentHTML,
  hasAnswerCommentAvailable,
  noteSaveEpochsSettled,
  extractAnswerCommentSiblingHtml,
  docBodyRequiresAnswerCommentInDom,
  snapshotAnswerCommentSiblingHtml,
  resolveAnswerCommentSectionElement,
  restoreAnswerCommentHTML,
  removeAnswerCommentFromUI,
  setDisplayBlockByIds,
  setDisplayNoneByIds,
  revealEndOfCaseCompletionBannerIfNeeded,
  showFinishCardContentAndSection,
  focusWhatsNextCardHeading,
  switchTwoColPaneToFinishSectionIfNeeded,
  submitSelectedStudentChoiceCourses,
  DISPLAY_MODES,
  normalizeNodeUid,
  getDusEntryForUid,
  resolveUnlockCurrentSectionUid,
  findAnswerCommentInActivePane,
  revealAnswerCommentElement,
  shouldPreserveAnswerCommentInDom,
  dispatchCaseExperienceRefreshContinueButtonsIfTwoCol,
  syncPanelStickyWithSideNavLayout,
  scrollOpenedCardTitleIntoView,
  scheduleScrollOpenedCardTitleIntoView,
  feedbackCardRatingsIncomplete,
};
